import {
    IContentTier,
    IContentTierBenefit,
    IContentTiers,
    IContentTierBillingPlans
} from '@dazn/lp-web/src/types/contentTiers.types';
import {getImage} from '@dazn/lp-web/src/utils/commonUtils';
import {transformContentTierButton} from '@dazn/lp-web/src/proxies/contentTierButton.proxy';
import {STATIC_IMAGE_URL} from '@dazn/lp-web/src/types/common.types';
import {getLocale} from '../utils/dazn/Region';
import { getContentTierVersion } from '../components/Header/Utils/features';

function transformContentTierBenefits(
    response: any
): Array<IContentTierBenefit> {
    const benefits = new Array<IContentTierBenefit>();

    response?.forEach((benefit: any) => {
        const contentTierBenefit = {} as IContentTierBenefit;
        const iconKey = benefit?.fields?.key;
        contentTierBenefit.disabled = iconKey?.indexOf('disabled') > -1;
        contentTierBenefit.iconName = iconKey;
        contentTierBenefit.value = benefit?.fields?.value;
        contentTierBenefit.iconUrl = iconKey
            ? STATIC_IMAGE_URL + iconKey + '.png'
            : '';
        benefits.push(contentTierBenefit);
    });

    return benefits;
}

function transformContentTierOverrideLogos(response: any): string[] {
    const logos: string[] = [];

    if (!response) return logos;

    response?.forEach((logo: any) => {
        const image = getImage(logo);
        image?.src && logos.push(image.src);
    });

    return logos;
}

function parseOverrideTextByLocale(text: string) {
    try {
        if (!text) return '';

        const parsedText = JSON.parse(text);

        if (Object.prototype.toString.call(parsedText) === '[object Object]') {
            for (const key in parsedText) {
                if (key.includes(getLocale())) {
                    return parsedText[key];
                }
            }

            return parsedText['default'];
        }

        return text;
    } catch (error) {
        return text;
    }
}

function transformContentTierBillingPlans(
    response: any
): IContentTierBillingPlans[] {
    const billingPlans = new Array<IContentTierBillingPlans>();

    response?.forEach((plan: any) => {
        const contentTierBillingPlan = {} as IContentTierBillingPlans;
        contentTierBillingPlan.badgeText = plan?.fields?.badgeText || '';
        contentTierBillingPlan.label = plan?.fields?.label;
        contentTierBillingPlan.showBadge = plan?.fields?.showBadge;
        contentTierBillingPlan.billingPeriodType =
            plan?.fields?.billingPeriodType;
        billingPlans.push(contentTierBillingPlan);
    });

    return billingPlans;
}

function transformContentTier(response: any): Array<IContentTier> {
    const tiers = new Array<IContentTier>();

    response?.forEach((tier: any) => {
        const contentTier = {} as IContentTier;
        contentTier.title = parseOverrideTextByLocale(tier?.fields?.title);
        contentTier.description = parseOverrideTextByLocale(
            tier?.fields?.description
        );
        contentTier.annualDescription = parseOverrideTextByLocale(
            tier?.fields?.AnnualDescription
        );
        contentTier.monthlyDescription = parseOverrideTextByLocale(
            tier?.fields?.MonthlyDescription
        );
        contentTier.offerAnnualDescription = parseOverrideTextByLocale(
            tier?.fields?.OfferAnnualDescription
        );
        contentTier.offerMonthlyDescription = parseOverrideTextByLocale(
            tier?.fields?.OfferMonthlyDescription
        );
        contentTier.isCardHighlighted = tier?.fields?.isCardHighlighted;
        contentTier.showBestValueBadge = tier?.fields?.showBestValueBadge;
        contentTier.bestValueBadgeText = tier?.fields?.bestValueBadgeText;
        contentTier.bestValueBadgeTextInstallment =
            tier?.fields?.bestValueBadgeTextInstallment;
        contentTier.bestValueBadgeTextAnnual =
            tier?.fields?.bestValueBadgeTextAnnual;
        contentTier.bestValueBadgeTextMonthly =
            tier?.fields?.bestValueBadgeTextMonthly;
        contentTier.eyebrowText = tier?.fields?.eyebrowText;
        contentTier.showEyebrow =
            tier?.fields?.showEyebrow && contentTier.eyebrowText;
        contentTier.compareEntitlementSetId =
            tier?.fields?.compareEntitlementSetId;
        contentTier.compareBillingPeriod = tier?.fields?.compareBillingPeriod;
        contentTier.entitlementSetId = tier?.fields?.entitlementSetId;
        contentTier.billingPeriod = tier?.fields?.billingPeriod;
        contentTier.showPrice = tier?.fields?.showPrice;
        contentTier.price = parseOverrideTextByLocale(tier?.fields?.price);
        contentTier.yearlyprice = parseOverrideTextByLocale(
            tier?.fields?.yearlyprice
        );
        contentTier.monthlyprice = parseOverrideTextByLocale(
            tier?.fields?.monthlyprice
        );
        contentTier.freeTrialText = tier?.fields?.freeTrialText;
        contentTier.showBadge = tier?.fields?.showBadge;
        contentTier.badgeText = tier?.fields?.badgeText;
        contentTier.badgeColor = tier?.fields?.badgeColor;
        contentTier.showImage = tier?.fields?.showImage;
        contentTier.image = getImage(tier?.fields?.image);
        contentTier.interactionType = tier?.fields?.interactionType;
        contentTier.monthText = tier?.fields?.monthText;
        contentTier.weekText = tier?.fields?.weekText;
        contentTier.installmentText = tier?.fields?.installmentText;
        contentTier.yearText = tier?.fields?.yearText;
        contentTier.overrideBadgeText = tier?.fields?.overrideBadgeText;
        contentTier.button = transformContentTierButton(
            tier?.fields?.buttons?.length > 0
                ? tier?.fields?.buttons?.[0]
                : null
        );
        contentTier.showDivider = tier?.fields?.showDivider;
        contentTier.dividerText = tier?.fields?.dividerText;
        contentTier.showLogos = tier?.fields?.showLogos;
        contentTier.showOverrideLogos = tier?.fields?.showOverrideLogos;
        contentTier.overrideLogos =
            tier?.fields?.showOverrideLogos &&
            tier?.fields?.overrideLogos?.length > 0
                ? transformContentTierOverrideLogos(tier?.fields?.overrideLogos)
                : [];
        contentTier.showBenefits = tier?.fields?.showBenefits;
        contentTier.benefits = transformContentTierBenefits(
            tier?.fields?.benefits
        );
        tiers.push(contentTier);
    });

    return tiers;
}

export function contentTiersProxy(response: any): IContentTiers {
    const data = response?.fields?.entries?.[0]?.fields;
    let contentTiers = {} as IContentTiers;

    contentTiers.title = data?.title;
    contentTiers.freeTrialTitle = data?.freeTrialTitle;
    contentTiers.showPlanUI = data?.showPlanUI;
    contentTiers.billingPlans = transformContentTierBillingPlans(
        data?.billingPlans
    );
    contentTiers.description = data?.description;
    contentTiers.freeTrialDescription = data?.freeTrialDescription;
    contentTiers.freeTrialMobileDescription = data?.freeTrialMobileDescription;
    contentTiers.backgroundImage = getImage(data?.backgroundImage);
    contentTiers.showBillingPeriodSwitcher = data?.showBillingPeriodSwicther;
    contentTiers.defaultBillingPlan = data?.defaultBillingPlan;
    contentTiers.preselectBillingPeriod = data?.preselectBillingPeriod;
    contentTiers.showMoreDescriptionLabel = data?.showMoreDescriptionLabel;
    contentTiers.tiers = transformContentTier(data?.tiers);
    contentTiers.showBenefits = data?.showBenefits;
    contentTiers.showSeeMorePlans = data?.showSeeMore;
    contentTiers.showWeeklyTierAside = data?.showWeeklyTierAside;
    contentTiers.showWeeklyTierTop = data?.showWeeklyTierTop;
    contentTiers.bottomDividerText = data?.bottomDividerText;
    contentTiers.seeMorePlansText = data?.seeMoreText;
    contentTiers.closeSeeMoreText = data?.closeSeeMoreText;
    contentTiers.benefits = transformContentTierBenefits(data?.benefits);
    contentTiers.componentType = response?.fields?.componentType;
    contentTiers.version = data?.tierVersion || 'v1' || getContentTierVersion();
    contentTiers.entitlementSetId = data?.entitlementSetId || '';
    contentTiers.bestValueBadgeShown =
        contentTiers?.tiers
            ?.map((m) => m.showBestValueBadge && m.bestValueBadgeText)
            .filter((m) => m)?.length > 0;

    if (
        window.innerWidth < 768 &&
        !data?.showDefaultOrderMobile &&
        data?.tierVersion !== 'v2'
    ) {
        contentTiers.tiers = contentTiers.tiers.sort(function (
            a: IContentTier,
            b: IContentTier
        ) {
            if (
                (a.isCardHighlighted ||
                    (!a.isCardHighlighted && a.showEyebrow)) &&
                !(
                    b.isCardHighlighted ||
                    (!b.isCardHighlighted && b.showEyebrow)
                )
            ) {
                return -1;
            } else if (
                !(
                    a.isCardHighlighted ||
                    (!a.isCardHighlighted && a.showEyebrow)
                ) &&
                (b.isCardHighlighted || (!b.isCardHighlighted && b.showEyebrow))
            ) {
                return 1;
            } else {
                return 0;
            }
        });
    }

    return contentTiers;
}
